import { useEffect, useState } from 'react';
import { ArrowRight, ChevronRight, Plus, RotateCcw } from 'lucide-react';
import { ActionStack, Screen } from '@/app/layout/Screen';
import { Button } from '@/components/ui/Button';
import { PanelSkeleton } from '@/components/ui/Feedback';
import { Input } from '@/components/ui/Form';
import { Sheet } from '@/components/ui/Sheet';
import { ProfileAvatar } from '@/components/ui/Profile';
import { Badge, Kicker, Lead, MiniIcon, PageTitle, Panel, Row } from '@/components/ui/Surface';
import { canCreateProfile, useDoctorProfilesStore } from '@/store/doctorProfilesStore';
import { useBackendSurveyStore } from '@/store/backendSurveyStore';
import { useUiStore } from '@/store/uiStore';
import type { DoctorProfile } from '@/api/miniapp/types';

/** Двухбуквенный код профиля для аватара. */
function shortCode(name: string): string {
  const words = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  return ((name ?? '').trim().slice(0, 2) || 'ПР').toUpperCase();
}

/**
 * Выбор профессионального профиля на шаге регистрации.
 *
 * После выбора или создания перезагружаются вопросы и ответы: активный профиль
 * определяет данные анкеты. Переход к анкете — по кнопке, когда профиль выбран.
 */
export function RegistrationProfilesScreen() {
  const { profiles, activeId, loading, busy, load, select, create } = useDoctorProfilesStore();
  const canCreate = canCreateProfile(profiles);
  const reloadSurvey = useBackendSurveyStore((state) => state.reload);
  const toast = useUiStore((state) => state.toast);

  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');

  useEffect(() => {
    void load();
  }, [load]);

  const choose = async (profile: DoctorProfile) => {
    const ok = await select(profile.doctor_profile_id);
    if (ok) await reloadSurvey();
  };

  const submitNew = async () => {
    const profile = await create(name);
    if (profile) {
      setCreating(false);
      setName('');
      await reloadSurvey();
      toast('Профиль создан', 'success');
    } else {
      toast('Не удалось создать профиль', 'error');
    }
  };

  if (loading) {
    return (
      <Screen subtitle="Профиль" onboarding>
        <PanelSkeleton count={3} />
      </Screen>
    );
  }

  const hasProfiles = profiles.length > 0;

  return (
    <Screen subtitle="Профиль" onboarding>
      <Kicker>Профессиональный контекст</Kicker>
      <PageTitle>{hasProfiles ? 'Выберите профиль' : 'Создайте первый профиль'}</PageTitle>
      <Lead>
        У каждого профиля своя анкета, прогресс курса и материалы ИИ. Данные между профилями не смешиваются.
      </Lead>

      {profiles.map((profile) => {
        const isActive = profile.doctor_profile_id === activeId;
        return (
          <button
            key={profile.doctor_profile_id}
            type="button"
            disabled={busy}
            onClick={() => void choose(profile)}
            className="bd-focus mb-2.5 flex w-full items-center gap-2.5 rounded-panel border border-line/60 bg-surface p-3.5 text-left shadow-panel transition-transform active:scale-[.99] aria-[current=true]:border-brand aria-[current=true]:ring-2 aria-[current=true]:ring-brand/15"
            aria-current={isActive}
          >
            <ProfileAvatar short={shortCode(profile.profile_name)} size={42} />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[14px] font-medium">{profile.profile_name}</span>
            </span>
            {isActive ? (
              <Badge tone="success">Активный</Badge>
            ) : (
              <ChevronRight className="h-4 w-4 text-muted" aria-hidden />
            )}
          </button>
        );
      })}

      {canCreate ? (
        <ActionStack>
          <Button block icon={<Plus className="h-4 w-4" aria-hidden />} onClick={() => setCreating(true)}>
            {hasProfiles ? 'Создать новый профиль' : 'Создать профиль'}
          </Button>
        </ActionStack>
      ) : (
        <Panel tone="tint" className="mt-2.5">
          <Row
            icon={
              <MiniIcon tone="soft">
                <RotateCcw className="h-[17px] w-[17px]" aria-hidden />
              </MiniIcon>
            }
            title="Достигнут лимит"
            subtitle="Можно создать не больше трёх профилей."
          />
        </Panel>
      )}

      {activeId ? (
        <ActionStack>
          <Button
            variant="primary"
            block
            loading={busy}
            onClick={() => void reloadSurvey()}
            iconRight={<ArrowRight className="h-4 w-4" aria-hidden />}
          >
            Перейти к анкете
          </Button>
        </ActionStack>
      ) : null}

      <Sheet open={creating} onClose={() => setCreating(false)} title="Новый профиль">
        <Input
          label="Название профиля"
          placeholder="Например: Кардиология"
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
        <div className="mt-4 grid gap-2">
          <Button variant="primary" block loading={busy} disabled={name.trim().length < 2} onClick={submitNew}>
            Создать
          </Button>
          <Button variant="ghost" block onClick={() => setCreating(false)}>
            Отменить
          </Button>
        </div>
      </Sheet>
    </Screen>
  );
}
